import { useCallback, useRef, useState } from 'react';
import type { OrigemInteresse } from '@social/shared';
import {
  registrarInteresse,
  ErroInteresse,
  type DependenciasInteresse,
} from '../lib/interesse';

export type EstadoInteresse =
  | { fase: 'ocioso' }
  | { fase: 'enviando' }
  | { fase: 'sucesso' }
  | {
      fase: 'erro';
      codigo: string;
      mensagem: string;
      retryAfterSegundos?: number | undefined;
      campo?: string | undefined;
    };

/**
 * Estado da captura de e-mail: ocioso → enviando → sucesso | erro. Mesmo
 * padrão de `useAuditoria` — `dep` injetável só para o teste, `emVoo` ignora
 * um segundo envio enquanto o primeiro está no ar (clique duplo no botão).
 */
export function useInteresse(dep: DependenciasInteresse = { buscar: fetch }): {
  estado: EstadoInteresse;
  registrar: (
    email: string,
    origem: OrigemInteresse,
    consentimento: boolean,
  ) => Promise<void>;
} {
  const [estado, setEstado] = useState<EstadoInteresse>({ fase: 'ocioso' });
  const emVoo = useRef(false);

  const registrar = useCallback(
    async (email: string, origem: OrigemInteresse, consentimento: boolean) => {
      if (emVoo.current) return;
      emVoo.current = true;
      setEstado({ fase: 'enviando' });
      try {
        await registrarInteresse({ email, origem, consentimento }, dep);
        setEstado({ fase: 'sucesso' });
      } catch (erro) {
        if (erro instanceof ErroInteresse) {
          setEstado({
            fase: 'erro',
            codigo: erro.codigo,
            mensagem: erro.message,
            retryAfterSegundos: erro.retryAfterSegundos,
            campo: erro.campo,
          });
        } else {
          setEstado({
            fase: 'erro',
            codigo: 'rede',
            mensagem: 'Não foi possível conectar. Confira sua internet e tente de novo.',
          });
        }
      } finally {
        emVoo.current = false;
      }
    },
    [dep],
  );

  return { estado, registrar };
}
